import { ReactComponent as SparklesIcon } from 'assets/images/ai/ic-sparkles.svg'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React from 'react'
import { StyleObject, useStyletron } from 'styletron-react'
import { merchantTheme } from 'theme'

// Locale keys intentionally stay under recovery_ai.* even though the component
// now lives in components/: recoveryAi.ts is this release's busiest merge surface,
// so moving two keys into components.* buys nothing and costs a conflict.
const t = buildTranslate('recovery_ai.ai_generated_badge')

const MAGIC_WAND_SIZE_PX = 12
const SPARKLES_ICON_WIDTH_PX = 23
const SPARKLES_ICON_HEIGHT_PX = 20

// Shared pill geometry — only the fill/text colors differ per variant below.
const badgePillBase: StyleObject = {
  fontWeight: 700,
  fontSize: '10px',
  letterSpacing: '1px',
  textTransform: 'uppercase',
  padding: '2px 6px',
  borderRadius: '4px',
}

// Figma TUTD spec (h4mcORhHQEGfWaJ6yrtiDf, node 659-9447), also matched by the
// MX-RecoveryAI Escalations spec (2L1kCbUYlhHJqhmtNt0kpW, node 2318:64225): a
// light spearmint10 chip with spearmint50 text, paired with a larger sparkle
// cluster rendered *outside* the pill rather than a small glyph inside it.
// Opt-in via `appearance` (default stays "solid", the legacy look) so the
// other `variant="resolved"` consumer — RAI CaseDetail's RecentActivities
// ResolvedByCell — keeps its current pill without forking this component.
//
// a11y: same spearmint10-bg/spearmint50-text pair as aiRecommendedBadgeStyle
// below, and — like that pill — it is BELOW WCAG 2.1 AA (~2.05:1 vs the 4.5:1
// floor for 10px bold text): an intentional Figma-exact parity trade-off, not
// an oversight. See the contrast guard in index.contrast.test.tsx.
export const aiBadgeSubtleLabelStyle: StyleObject = {
  backgroundColor: palette.spearmint10,
  color: palette.spearmint50,
  fontWeight: 700,
  fontSize: '10px',
  letterSpacing: '1px',
  textTransform: 'uppercase',
  lineHeight: '12px',
  padding: '4px 8px',
  borderRadius: '4px',
}

// resolved ("THANX AI") — the solid-fill brand pill. Exported so a11y contrast is
// asserted directly (styletron css() is a no-op in tests, so the color never
// reaches the DOM). Dark grey100 text on the spearmint50 brand pill = 6.42:1,
// clearing WCAG 2.1 AA (4.5:1) for the 10px bold label.
export const aiBadgeLabelStyle: StyleObject = {
  ...badgePillBase,
  backgroundColor: merchantTheme.colors.spearmint50,
  color: merchantTheme.colors.grey100,
}

// created ("AI recommended") — the Figma-parity reward pill: a pale spearmint10
// tint with the brighter spearmint50 as the text color. The brighter teal on the
// light tint is BELOW WCAG AA (a deliberate product decision to match the Figma
// exactly — see the Approved-rewards redesign), so — unlike aiBadgeLabelStyle —
// this style is intentionally not contrast-guarded. It also drops the fa-magic
// sparkle (the reward rows carry no icon in the design).
export const aiRecommendedBadgeStyle: StyleObject = {
  ...badgePillBase,
  backgroundColor: merchantTheme.colors.spearmint10,
  color: merchantTheme.colors.spearmint50,
}

type Props = {
  variant?: 'created' | 'resolved'
  appearance?: 'solid' | 'subtle'
  // Overrides the variant/appearance-derived icon below. For a caller with its
  // own Figma-specified sparkle (e.g. GrantRewardModal's dropdown pill, which
  // wants a sparkle NEXT TO "created" copy — the variant/appearance matrix
  // otherwise keeps `created` icon-free by design, see isCreated below).
  // undefined (the default) keeps the existing derived icon; null renders none.
  icon?: React.ReactElement | null
}

// RAI sparkle badge — factored from the two ResolvedByCell pills (Activity +
// CaseDetail/RecentActivities), which each hand-rolled this exact spearmint
// "THANX AI" pill + fa-magic idiom. Deliberately NOT `components/AIBadge`: that
// is the generic Segments/CampaignCenter text-only "AI" pill (hex-styled scss),
// off-idiom for RecoveryAI's fa-magic/spearmint50 sparkle.
//   - variant="created" (default) → "AI recommended" — a reward Thanx AI
//     authored (the make-good templates, gated on AvailableReward.ai_generated).
//   - variant="resolved"          → "THANX AI"            — the ResolvedByCell
//     marker that the agent (not an operator) closed the case.
//   - appearance="solid" (default) → legacy pill + inline fa-magic glyph.
//   - appearance="subtle"          → Figma TUTD pill + trailing sparkle icon.
const AiGeneratedBadge = ({
  variant = 'created',
  appearance = 'solid',
  icon: iconOverride,
}: Props): React.ReactElement => {
  const [css] = useStyletron()
  const isSubtle = appearance === 'subtle'

  // The created reward pill uses the pale tint + no sparkle (Figma parity); the
  // resolved "THANX AI" marker keeps the solid fill + its decorative sparkle
  // (either the inline fa-magic glyph, or — when appearance="subtle" — the
  // standalone sparkle cluster). isCreated wins over isSubtle: no current
  // consumer passes both, but the reward pill's icon-free Figma parity should
  // hold regardless of appearance — unless the caller opts in via an explicit
  // `icon` override (see Props above; GrantRewardModal's dropdown pill does).
  const isCreated = variant === 'created'

  let badgeStyle = aiBadgeLabelStyle
  if (isCreated) {
    badgeStyle = aiRecommendedBadgeStyle
  } else if (isSubtle) {
    badgeStyle = aiBadgeSubtleLabelStyle
  }

  let defaultIcon: React.ReactElement | null = (
    <i
      className={`fa fa-magic ${css({
        color: merchantTheme.colors.spearmint50,
        fontSize: `${MAGIC_WAND_SIZE_PX}px`,
      })}`}
      aria-hidden="true"
    />
  )
  if (isCreated) {
    defaultIcon = null
  } else if (isSubtle) {
    defaultIcon = (
      <SparklesIcon
        aria-hidden="true"
        className={css({
          width: `${SPARKLES_ICON_WIDTH_PX}px`,
          height: `${SPARKLES_ICON_HEIGHT_PX}px`,
          flexShrink: 0,
        })}
      />
    )
  }
  const icon = iconOverride !== undefined ? iconOverride : defaultIcon

  return (
    <span className={`d-flex align-items-center ${css({ gap: '4px' })}`}>
      <span className={css(badgeStyle)}>{t(variant)}</span>
      {/* Decorative sparkle — the label text already conveys the meaning. The
          created reward pill is icon-free by design, unless the caller passes
          its own `icon` override (see Props). */}
      {icon}
    </span>
  )
}

export default AiGeneratedBadge
